import type { CalendarEvent, ConflictResult, ConflictRule } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';

/**
 * Detects conflicts between scheduled persons and holiday calendar events.
 *
 * For each calendar event date, checks if any person is assigned in the schedule
 * on that date AND is in the event's `applicablePersonIds` set.
 * A person at location A is never flagged for a holiday from location B's calendar.
 *
 * Returns a ConflictResult per person-per-holiday overlap (only for applicable persons).
 */
export class HolidayConflictRule implements ConflictRule {
  readonly type = 'holiday' as const;

  detect(schedule: ScheduleMap, events: CalendarEvent[]): ConflictResult[] {
    const results: ConflictResult[] = [];

    // Filter events to only holiday type
    const holidayEvents = events.filter(e => e.type === 'holiday');

    for (const event of holidayEvents) {
      const daySchedule = schedule[event.date];
      if (!daySchedule) continue;

      // Build set of applicable person IDs for O(1) lookup
      const applicableSet = new Set(event.applicablePersonIds);

      // Check each scheduled person
      for (const personId of Object.keys(daySchedule)) {
        if (applicableSet.has(personId)) {
          results.push({
            type: 'holiday',
            date: event.date,
            personId,
            eventLabel: event.label,
            calendarId: event.calendarId,
          });
        }
      }
    }

    return results;
  }
}
